import { Button } from '@progress/kendo-react-buttons';
import { ComboBox, ComboBoxChangeEvent } from '@progress/kendo-react-dropdowns';
import { Error, Label } from '@progress/kendo-react-labels';
import {
  TabStrip,
  TabStripSelectEventArguments,
  TabStripTab
} from '@progress/kendo-react-layout';
import { Row } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { StandardInput } from '../../components/forms';
import { BreadcrumbLocation, PrivatePage } from '../../components/private-page';
import { useAuth } from 'react-oidc-context';
import { appStore } from '../../stores/app-store';
import { useCallback, useEffect, useState } from 'react';
import { Organisation } from '../../types/organisation';
import React from 'react';
import { InviteUserAsync } from '../../services/users';
import { PostEmailAvailabilityAsync } from '../../services/user-email';
import { PostAdminUserRolesAsync } from '../../services/roles';
import { InputChangeEvent } from '@progress/kendo-react-inputs';
import { Permission, PermissionType } from '../../types/permission';
import Toolbar from '../../components/page-toolbar';
import { GetAllAsync } from '../../services/api';
import { PutAppNotificationByUserIdAsync } from '../../services/notification';
import { NotificationName } from '../../types/responses/notification-type-response';
import UserPermission from '../../components/permission';
import { useToast } from '../../contexts/toast-context';

export default function AdminInviteUser() {
  const { pathname } = useLocation();
  const isFacilitator = pathname.includes('facilitators');

  const auth = useAuth();
  const navigate = useNavigate();
  const breadcrumbs: BreadcrumbLocation[] = [
    { label: 'Dashboard', href: '/' },
    { label: 'Administration', href: '../administration' },
    {
      label: isFacilitator ? 'Facilitator users' : 'Primary users',
      href: isFacilitator
        ? '../administration/facilitators'
        : '../administration/users'
    }
  ];

  const orgId = parseInt(auth.user?.profile.catalyst_org_id! as string);
  const [selected, setSelected] = React.useState<number>(0);
  const [emailError, setEmailError] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [organisationId, setOrganisationId] = useState<number>(orgId);
  const [organisationList, setOrganisationList] = useState<Organisation[]>([]);
  const [userPermission, setUserPermission] = useState<string[]>();
  const handleSelect = (e: TabStripSelectEventArguments) => {
    setSelected(e.selected);
  };
  const { onToastOpen } = useToast();

  const onBackHandler = () => {
    navigate(
      isFacilitator ? '/administration/facilitators' : '/administration/users'
    );
  };

  const getOrganisations = async () => {
    GetAllAsync('organisations')
      .then((response) => {
        setOrganisationList(response.data as Organisation[]);
      })
      .catch((err) => console.error(err));
  };

  const onChangeOrganisationHandler = (e: ComboBoxChangeEvent) => {
    if (e?.value?.id !== undefined) {
      setOrganisationId(parseInt(e?.value?.id));
    }
  };

  const isEmailValid = (value: string) => {
    const emailRegex: RegExp = new RegExp(/\S+@\S+\.\S+/);

    return emailRegex.test(value) ? '' : 'Please enter a valid email.';
  };

  const emailChangeHandler = (e: InputChangeEvent) => {
    setEmail(e.target.value?.toString() ?? '');
  };

  const onPermissionChange = useCallback(
    (permission: string, value: boolean) => {
      // Define parent permissions
      const parentPermissions = [
        'Admin.PendingApproval',
        'Admin',
        'GlobalLibrary',
        'MyLibrary',
        'Network',
        'Organisation',
        'OrgAdmin',
        'App',
        'Events',
        'Reports',
        'Facilitator'
      ];

      if (value) {
        setUserPermission((prev) => {
          const userRoles = (auth.user?.profile.role as string[]) || [];
          let addedPermission = parentPermissions.includes(permission)
            ? userRoles.filter((role) => {
                return role === permission || role.startsWith(permission + '.');
              })
            : [permission];

          parentPermissions.forEach((parent) => {
            if (
              permission.startsWith(parent + '.') &&
              !prev?.includes(parent)
            ) {
              addedPermission.push(parent);
            }
          });

          return [
            ...(prev as PermissionType[]),
            ...(addedPermission as PermissionType[])
          ];
        });
      } else {
        setUserPermission((prev) => {
          if (!prev) return [];

          const newPermissions = new Set(prev);
          const isParent =
            !permission.includes('.') || permission === 'Admin.PendingApproval';
          const parts = permission.split('.');

          if (isParent) {
            const permissionsToRemove = [...newPermissions].filter((p) =>
              p.startsWith(permission)
            );
            permissionsToRemove.forEach((p) => newPermissions.delete(p));

            if (parts.length === 2) {
              const hasChildren = [...newPermissions].some((p) =>
                p.startsWith(parts[0] + '.')
              );
              if (!hasChildren) {
                newPermissions.delete(parts[0]);
              }
            }
          } else {
            newPermissions.delete(permission);

            let parent: string;
            if (parts.length === 3) {
              parent = `${parts[0]}.${parts[1]}`;
              const hasChildren = [...newPermissions].some((p) =>
                p.startsWith(parent + '.')
              );
              if (!hasChildren) {
                newPermissions.delete(parent);
              }
            }

            parent = parts[0];
            const hasOtherChildren = [...newPermissions].some((p) =>
              p.startsWith(parent + '.')
            );

            if (!hasOtherChildren) {
              newPermissions.delete(parent);
            }
          }

          return Array.from(newPermissions);
        });
      }
    },
    [auth.user?.profile.role]
  );

  const onClickInviteHandler = async () => {
    try {
      appStore.showLoading();

      let isValidEmail = isEmailValid(email!);

      if (email === '') {
        let emailEmpty = 'Email is required.';
        onToastOpen({
          title: 'Email',
          body: <div>{emailEmpty}</div>,
          style: 'error'
        });

        setEmailError(emailEmpty);
        return;
      }

      if (isValidEmail !== '') {
        onToastOpen({
          title: 'Email',
          body: <div>{isValidEmail}</div>,
          style: 'error'
        });

        setEmailError(isValidEmail);
        return;
      }

      const available = await PostEmailAvailabilityAsync({
        emailAddress: email!
      });

      if (available) {
        let response = await InviteUserAsync(organisationId! ?? orgId, {
          firstName: '',
          lastName: '',
          emailAddress: { address: email },
          type: isFacilitator ? 'FacilitatorOnly' : 'Standard'
        });

        if (response.id !== undefined) {
          const inAppNotification = [
            'Event.Created',
            'Event.Completed',
            'Game.Created',
            'Game.Published',
            'Game.Submitted',
            'Game.Approved',
            'Game.Duplicated',
            'Assessment.Created',
            'Assessment.Published',
            'Assessment.Submitted',
            'Assessment.Approved',
            'Assessment.Duplicated',
            'Feedback.Created',
            'Feedback.Published',
            'Feedback.Submitted',
            'Feedback.Approved',
            'Feedback.Duplicated',
            'GlobalLibrary.Game.Added',
            'GlobalLibrary.Assessment.Added',
            'GlobalLibrary.Feedback.Added'
          ];

          // set all in-app notification settings on for new registered user
          await PutAppNotificationByUserIdAsync(
            response?.id,
            inAppNotification as NotificationName[]
          );

          await PostAdminUserRolesAsync(
            organisationId! ?? orgId,
            response?.id,
            userPermission!
          );
          setEmail('');
        }

        onToastOpen({
          title: `Invite ${isFacilitator ? 'facilitator' : 'user'}`,
          body: (
            <div>
              {isFacilitator ? 'Facilitator' : 'User'} added.<br></br>
              <Link
                to={
                  isFacilitator
                    ? '/administration/facilitators'
                    : '/administration/users'
                }>
                Go to {isFacilitator ? 'facilitator users' : 'primary users'}.
              </Link>
            </div>
          ),
          style: 'success'
        });
      } else {
        onToastOpen({
          title: isFacilitator ? 'Facilitator users' : 'Primary users',
          body: <div>{`Email already registered.`}</div>,
          style: 'error'
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      appStore.hideLoading();
    }
  };

  const defaultPermissions = () => {
    let userDefaultPermissions: string[] = [];
    (auth.user?.profile.role as PermissionType[]).forEach((f) => {
      if (isFacilitator) {
        if (
          ['Facilitator', 'Facilitator.Portal', 'Facilitator.Spark', 'Facilitator.Assessment'].includes(f)
        )
          userDefaultPermissions.push(f);
      } else {
        if (!f.includes('Admin')) userDefaultPermissions.push(f);
      }
    });
    setUserPermission(userDefaultPermissions!);
  };

  const childPermission = (auth.user?.profile.role as PermissionType[]).filter(
    (permission, index) =>
      permission.split('.')[0] === Permission.Admin && index > 0
  );

  const permissionKeyList = Object.keys(Permission) as PermissionType[];

  const currentUserRoles = auth.user?.profile.role as PermissionType[];

  const childUserRoles = (Object.keys(Permission) as PermissionType[]).filter(
    (permission, index) =>
      permission.split('.').length > 1 &&
      permission.split('.')[0] === Permission.Admin &&
      index > 0
  );

  const setRoleToDisable = (permission?: string, isMenuAdmin?: boolean) => {
    let result: PermissionType | undefined;
    result = currentUserRoles.find(
      (role) =>
        role.includes(permission!) &&
        (isMenuAdmin || (!isMenuAdmin && !role.includes('Admin')))
    );

    if (result) return false;
    return true;
  };

  useEffect(() => {
    getOrganisations();
    defaultPermissions();
  }, []);

  return (
    <PrivatePage
      breadcrumb={breadcrumbs}
      pageTitle={isFacilitator ? 'Facilitator users' : 'Primary users'}>
      <Toolbar title={`New ${isFacilitator ? 'Facilitator' : 'User'}`}>
        <Button
          className={'me-1'}
          themeColor={'secondary'}
          onClick={() => onBackHandler()}>
          Back
        </Button>
        <Button
          className={'me-1'}
          themeColor={'primary'}
          onClick={onClickInviteHandler}>
          Send invite
        </Button>
      </Toolbar>
      <Row className={'mt-3'}>
        <TabStrip selected={selected} onSelect={handleSelect}>
          <TabStripTab title={'Info'}>
            <div>
              <p>
                New users will receive an email to complete their log in
                details.
              </p>
              <StandardInput
                label={'Email'}
                value={email ?? ''}
                onChange={emailChangeHandler}></StandardInput>
              <Error>{emailError}</Error>

              <Label> Organisation</Label>
              <ComboBox
                data={organisationList}
                textField={'name'}
                filterable={true}
                clearButton={false}
                value={organisationList?.find((x) => x.id === organisationId)}
                onChange={onChangeOrganisationHandler}
                disabled={false}
              />
            </div>
          </TabStripTab>
          <TabStripTab title={'Permissions'}>
            <UserPermission
              permissions={
                isFacilitator
                  ? ([
                      'Facilitator',
                      'Facilitator.Portal',
                      'Facilitator.Spark',
                      'Facilitator.Assessment',
                      'App',
                      'App.N4S',
                      'App.DroneControl',
                      'App.H4C',
                      'App.BTB',
                      'App.QuickFire',
                      'App.BP'
                    ] as PermissionType[])
                  : (auth.user?.profile.role as PermissionType[])
              }
              userPermissions={userPermission as PermissionType[]}
              onChange={onPermissionChange}
              showAdminPermission={!isFacilitator}
              isFacilitator={isFacilitator}
              isAdmin={true}
            />
          </TabStripTab>
        </TabStrip>
      </Row>
    </PrivatePage>
  );
}
